import { useState, useEffect } from "react";
import { ImageOff, AlertCircle } from "lucide-react";
import CountrySelector from "./CountrySelector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { supabase } from "@/integrations/supabase/client";

interface CountryHeroProps {
  countryName: string;
  backgroundImageUrl?: string;
  backgroundImageAlt?: string;
  showSelector?: boolean; // New prop to control selector visibility
}

const CountryHero = ({
  countryName,
  backgroundImageUrl,
  backgroundImageAlt,
  showSelector = true, // Default to true for backward compatibility
}: CountryHeroProps) => {
  const [imageError, setImageError] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(!!backgroundImageUrl);
  const [publicUrl, setPublicUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Reset state when background image URL changes
    if (backgroundImageUrl) {
      setIsImageLoading(true);
      setImageError(false);

      // Convert the URL to a public URL if it's from Supabase storage
      const url = getPublicUrl(backgroundImageUrl);
      setPublicUrl(url);

      const img = new Image();
      img.onload = () => {
        setIsImageLoading(false);
        setImageError(false);
      };
      img.onerror = () => {
        console.error("Failed to load background image:", url);
        setIsImageLoading(false);
        setImageError(true);
      };
      img.src = url;
    } else {
      setIsImageLoading(false);
    }
  }, [backgroundImageUrl]);

  // Convert Supabase storage URL to direct public URL
  const getPublicUrl = (url?: string) => {
    if (!url) return undefined;

    // Check if this is a relative path to public directory
    if (url.startsWith("/img/")) {
      return url; // Use as is for public directory images
    }

    // Handle different URL formats from Supabase storage
    if (url.includes("/storage/v1/object/public/")) {
      // Already a public URL, return as is
      return url;
    } else if (url.includes("/storage/v1/object/sign/")) {
      // Convert signed URL to public URL
      return url.replace(
        "/storage/v1/object/sign/",
        "/storage/v1/object/public/"
      );
    } else if (url.startsWith("/")) {
      // Relative URL, prepend Supabase URL
      return `${SUPABASE_URL}${url}`;
    }

    return url;
  };

  // Determine which background to use
  const backgroundStyle =
    imageError || !publicUrl
      ? {
          background:
            "linear-gradient(to right, var(--background), var(--muted))",
        }
      : {
          backgroundImage: `url(${publicUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        };

  return (
    <section
      className={`relative py-16 md:py-24 border-b border-border min-h-[300px] ${
        isImageLoading ? "animate-pulse" : ""
      }`}
      style={backgroundStyle}
    >
      {/* Add overlay for text readability when image is present */}
      {!imageError && publicUrl && (
        <div className="absolute inset-0 bg-black/10 z-[1]" />
      )}

      {imageError && backgroundImageUrl && (
        <div className="absolute top-4 right-4 z-10">
          <Alert variant="destructive" className="max-w-md p-2">
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertDescription className="text-xs">
              Failed to load country image. Using fallback.
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="container mx-auto px-4 relative z-[2]">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div
            className={
              !imageError && publicUrl ? "text-white" : "text-foreground"
            }
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              {countryName} Cards (Beta)
            </h1>
            <p
              className={`max-w-2xl ${
                !imageError && publicUrl
                  ? "text-gray-100"
                  : "text-muted-foreground"
              }`}
            >
              See what {countryName} thinks. Help shape what happens next.
            </p>
          </div>

          {/* Only render the CountrySelector if showSelector is true */}
          {showSelector && (
            <div className="flex-shrink-0">
              <CountrySelector />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CountryHero;
